import { AuthMessageType, ClientType, ConnectionMessage, ConnectionMessageType, websocket } from '@playcastdotio/Messaging';
import { getStateStore } from '../../../stores';
import { getElectronStores } from '../../../stores/electronStores';
import * as Sentry from '@sentry/react';
import { attachSentryTraceToMessage } from '../tracing';
import { setPendingHostAuth } from '../pendingHostAuth';

const MACHINE_ID_TIMEOUT_MS = 10000;
const electronStores = getElectronStores();

export const handleConnectionMessage = async (message: ConnectionMessage, rawSocket: WebSocket | null) => {
  console.log('Connection message:', message);
  const state = getStateStore('ipc');
  const accessToken = state.authState.currentState.accessToken;

  // Check if running in guest mode via state store (single source of truth)
  const isGuestMode = state.hostMatchmakingState.currentState.guestAccount;

  switch (message.type) {
    case ConnectionMessageType.CONN_AUTH_REQUIRED: {
      console.log('Auth required', { isGuestMode });
      const machineId = await new Promise<string>((resolve, reject) => {
        let settled = false;
        let listenerRef: ReturnType<typeof electronStores.machineIdState.addListener> | undefined;

        const timeoutId = setTimeout(() => {
          if (!settled) {
            settled = true;
            if (listenerRef) electronStores.machineIdState.removeListener(listenerRef);
            reject(new Error(`Machine ID not available within ${MACHINE_ID_TIMEOUT_MS}ms`));
          }
        }, MACHINE_ID_TIMEOUT_MS);

        const settle = (id: string) => {
          if (settled) return;
          settled = true;
          clearTimeout(timeoutId);
          resolve(id);
        };

        if (electronStores.machineIdState.currentState) {
          const currentId = electronStores.machineIdState.currentState;
          console.log('[CONNECTION] Using cached machine ID:', currentId);

          if (!currentId || currentId.trim() === '') {
            console.error('[CONNECTION] CRITICAL: Machine ID is empty! This will cause authentication to fail.');
            Sentry.captureException(new Error('Empty machineId in connection auth'), {
              level: 'error',
              tags: { issue_type: 'empty_machine_id_connection' },
              extra: { machineId: currentId || '(undefined)', timestamp: Date.now() },
            });
          } else if (currentId.startsWith('ERROR_')) {
            console.error('[CONNECTION] CRITICAL: Machine ID indicates an error condition:', currentId);
            Sentry.captureException(new Error(`Error machineId in connection: ${currentId}`), {
              level: 'error',
              tags: { issue_type: 'error_machine_id_connection' },
              extra: { machineId: currentId, timestamp: Date.now() },
            });
          }

          settle(currentId);
        } else {
          console.log('[CONNECTION] Waiting for machine ID - not yet available');
          listenerRef = electronStores.machineIdState.addListener((id) => {
            if (listenerRef) electronStores.machineIdState.removeListener(listenerRef);
            console.log('[CONNECTION] Received machine ID from listener:', id);
            settle(id);
          });
        }
      });
      // Guest mode: Use anonymous auth with machineId only
    // Standard mode: Use JWT token authentication
      const cloudHostToken = window.playcast?.cloudHostToken || undefined;
      // supportsAttestationChallenge: opt into AIK AUTH_CHALLENGE. Old clients omit
      // it and are tolerated server-side for now. After minimum installed registry
      // version blocks those builds, realtime-api can assume true without reading
      // the field; then stop sending this flag here.
      const msg = isGuestMode
        ? websocket.generators.auth(AuthMessageType.AUTH_REQUEST, {
            clientType: ClientType.HOST,
            machineId,
            anonymous: true, // Flag for anonymous authentication
            cloudHostToken,
            supportsAttestationChallenge: true,
          })
        : websocket.generators.auth(AuthMessageType.AUTH_REQUEST, {
            clientType: ClientType.HOST,
            tokenJwt: accessToken,
            machineId,
            cloudHostToken,
            supportsAttestationChallenge: true,
          });

      console.log('[Connection] Sending auth request:', { isGuestMode, hasToken: !!accessToken, machineId });

      setPendingHostAuth({
        machineId,
        isGuestMode,
        accessToken,
        cloudHostToken,
        rawSocket,
      });

      // Attach Sentry trace context for connection tracking
      Sentry.startSpan(
        {
          name: 'connection.auth.request',
          op: 'connection',
          attributes: {
            'connection.machineId': machineId,
            'connection.clientType': ClientType.HOST,
            'connection.isGuestMode': isGuestMode,
            'connection.anonymous': isGuestMode,
          },
        },
        () => {
          attachSentryTraceToMessage(msg);
          // Bypass ready check
          rawSocket?.send(JSON.stringify(msg));
        }
      );
    }
  }
};
